import { FC, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
import classNames from 'classnames';
// Import Types
import type { TabConfig } from 'types';
// Import UI Components
import { Paper } from '@geneui/components';
// Import Components
import DownloadOrSendToEmailToolbar from 'components/common/DownloadOrSendToEmailToolbar';
import PlayersDataTable from 'components/common/PlayersDataTable';
import CustomTabs from 'components/common/CustomTabs';
import BonusBlocksReport from './BonusBlocksReport';
// Import Configs
import { CampaignBonusReceivedPlayersTableConfig } from '../config';
// Import Constants
import { l } from 'constants/common';
// Import SCSS
import styles from './cjBonusReceivedPlayersSection.module.scss';

const SubTabKeys = {
    Players: 'Players',
    Blocks: 'Blocks',
} as const;

type SubTabKey = (typeof SubTabKeys)[keyof typeof SubTabKeys];

interface Props {
    workflowId: string;
    isActive?: boolean;
    isLoadingPlayers?: boolean;
    playersData?: Nullable<AnyObject>;
    downloadAllClickHandler: () => void;
    sendToEmailClickHandler: () => void;
    isFileLoading?: boolean;
}

const CJBonusReceivedPlayersSection: FC<Props> = ({
    workflowId,
    isActive = true,
    isLoadingPlayers = true,
    playersData = {},
    downloadAllClickHandler,
    sendToEmailClickHandler,
    isFileLoading = false,
}) => {
    const { t } = useTranslation();
    const [activeSubTab, setActiveSubTab] = useState<SubTabKey>(SubTabKeys.Players);

    const isPlayersTab = activeSubTab === SubTabKeys.Players;

    const tabsConfig: TabConfig[] = [
        {
            tabKey: SubTabKeys.Players,
            tabTitle: t(l.BonusReceivedPlayers),
            tabContent: (
                <PlayersDataTable
                    key="CampaignExecuteId"
                    name={t(l.BonusReceivedPlayers)}
                    config={CampaignBonusReceivedPlayersTableConfig}
                    isLoading={isLoadingPlayers}
                    data={playersData as { Data: AnyObject[] }}
                />
            ),
        },
        {
            tabKey: SubTabKeys.Blocks,
            tabTitle: t(l.BonusBlocks),
            tabContent: <BonusBlocksReport workflowId={workflowId} isActive={isActive} />,
        },
    ];

    return (
        <Paper
            className={classNames('c-r-s-with-title', styles.section)}
            paperDirection="column"
            cornerRadius="full-radius"
            border={true}
        >
            <CustomTabs
                activeTabKey={activeSubTab}
                tabsConfig={tabsConfig}
                tabChangeHandler={(key) => setActiveSubTab(key as SubTabKey)}
                customCssClassNames={styles.subTabs}
            />
            {isPlayersTab && (
                <div className={styles.toolbarOverlay}>
                    <DownloadOrSendToEmailToolbar
                        downloadAction={downloadAllClickHandler}
                        sendToEmailAction={sendToEmailClickHandler}
                        isFileLoading={isFileLoading}
                        isDisabledDownload={isNil(playersData?.Count) || playersData?.Count === 0}
                    />
                </div>
            )}
        </Paper>
    );
};

export default memo(CJBonusReceivedPlayersSection);
